import { Asset } from './AssetService';

export type CoordinateFrameId =
  | 'panorama_raw'
  | 'rvt_model'
  | 'ifc_model'
  | 'site_local'
  | 'geotiff_geo'
  | 'geotiff_pixel'
  | string;

export interface CoordinatePoint3D {
  x: number;
  y: number;
  z: number;
}

export interface PlanarSimilarityTransform {
  sourceFrame: CoordinateFrameId;
  targetFrame: CoordinateFrameId;
  translation: CoordinatePoint3D;
  rotationDeg: number;
  scale: CoordinatePoint3D;
  swapXY: boolean;
  flipX: boolean;
  flipY: boolean;
  flipZ: boolean;
}

export type CoordinateUnit = 'm' | 'ft' | 'mm' | 'unknown' | string;

export interface CoordinateFrameMetadata {
  id?: CoordinateFrameId;
  kind?: string;
  unit?: CoordinateUnit;
  source?: string;
  bounds?: {
    min?: Partial<CoordinatePoint3D>;
    max?: Partial<CoordinatePoint3D>;
  };
  globalOffset?: Partial<CoordinatePoint3D>;
  modelToViewerTransformAvailable?: boolean;
  poseSource?: string;
  setupCount?: number;
  coordinateMetadata?: string | null;
  updatedAt?: string;
  notes?: string[];
}

export interface LegacyGeotiffTransformConfig {
  xOffset: number;
  yOffset: number;
  coordinateScale: number;
  rotationOffsetDeg: number;
  movementScale: number;
  useTfw: boolean;
  useWorldCoordinates: boolean | null;
  swapAxes: boolean;
  flipX: boolean;
  flipY: boolean;
  disableCoordSync: boolean;
}

export interface AssetCoordinateTransformCatalog {
  transforms: {
    panoramaToRVTModel: PlanarSimilarityTransform;
  };
  legacy: {
    geotiff: LegacyGeotiffTransformConfig;
  };
}

export type CoordinateAlignmentStatus =
  | 'native'
  | 'native_with_unit_conversion'
  | 'legacy_override'
  | 'needs_calibration'
  | 'failed';

export type CoordinateTransformSource =
  | CoordinateAlignmentStatus
  | 'shared-reference'
  | 'model-bounds'
  | 'identity';

export interface CoordinateAlignmentDiagnostics {
  status: CoordinateAlignmentStatus;
  transformSource: CoordinateTransformSource;
  sourceFrame: CoordinateFrameMetadata;
  targetFrame: CoordinateFrameMetadata;
  viewerFrame: CoordinateFrameMetadata;
  transform: PlanarSimilarityTransform;
  unitScale: CoordinatePoint3D;
  hasLegacyOverride: boolean;
  notes: string[];
  updatedAt?: string;
  raw?: any;
}

interface TransformResolutionOptions {
  asset?: Asset | null;
  assetCustomData?: any;
  fallbackRVTModelBoundsMin?: CoordinatePoint3D | null;
  allowImplicitFallback?: boolean;
}

interface TransformResult {
  point: CoordinatePoint3D;
  transform: PlanarSimilarityTransform;
  transformSource: CoordinateTransformSource;
}

const ZERO_POINT: CoordinatePoint3D = { x: 0, y: 0, z: 0 };
const UNIT_SCALE: CoordinatePoint3D = { x: 1, y: 1, z: 1 };

const IDENTITY_PANORAMA_TO_RVT_TRANSFORM: PlanarSimilarityTransform = {
  sourceFrame: 'panorama_raw',
  targetFrame: 'rvt_model',
  translation: { ...ZERO_POINT },
  rotationDeg: 0,
  scale: { ...UNIT_SCALE },
  swapXY: false,
  flipX: false,
  flipY: false,
  flipZ: false
};

const coerceNumber = (value: any, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const coercePoint = (value: any, fallback: CoordinatePoint3D = ZERO_POINT): CoordinatePoint3D => {
  if (!value || typeof value !== 'object') {
    return { ...fallback };
  }

  return {
    x: coerceNumber(value.x, fallback.x),
    y: coerceNumber(value.y, fallback.y),
    z: coerceNumber(value.z, fallback.z)
  };
};

const coerceScale = (value: any): CoordinatePoint3D => {
  if (typeof value === 'number' || typeof value === 'string') {
    const parsed = coerceNumber(value, 1);
    return { x: parsed, y: parsed, z: parsed };
  }

  return {
    x: coerceNumber(value?.x, 1),
    y: coerceNumber(value?.y, 1),
    z: coerceNumber(value?.z, 1)
  };
};

const coerceBoolean = (value: any, fallback = false): boolean => {
  return typeof value === 'boolean' ? value : fallback;
};

const normalizeUnit = (unit: any): CoordinateUnit => {
  const value = String(unit || '').trim().toLowerCase();
  if (['m', 'meter', 'meters', 'metre', 'metres'].includes(value)) return 'm';
  if (['ft', 'feet', 'foot'].includes(value)) return 'ft';
  if (['mm', 'millimeter', 'millimeters', 'millimetre', 'millimetres'].includes(value)) return 'mm';
  return value || 'unknown';
};

const unitToMeters = (unit: CoordinateUnit): number | null => {
  const normalized = normalizeUnit(unit);
  if (normalized === 'm') return 1;
  if (normalized === 'ft') return 0.3048;
  if (normalized === 'mm') return 0.001;
  return null;
};

const getCustomData = (options: TransformResolutionOptions): Record<string, any> => {
  const direct = options.assetCustomData ?? options.asset?.custom_data;
  if (!direct) return {};

  if (typeof direct === 'string') {
    try {
      return JSON.parse(direct);
    } catch {
      return {};
    }
  }

  return direct;
};

const hasMeaningfulMagnitude = (point: CoordinatePoint3D | null | undefined): boolean => {
  if (!point) return false;
  return Math.max(Math.abs(point.x), Math.abs(point.y), Math.abs(point.z)) > 1000;
};

const hasExplicitPanoramaToRVTConfig = (customData: Record<string, any>): boolean => {
  if (customData.coordinate_transforms?.panorama_to_rvt_model || customData.panorama_to_rvt_model) {
    return true;
  }

  return [
    'panorama_to_rvt_offset_x',
    'panorama_to_rvt_offset_y',
    'panorama_to_rvt_offset_z',
    'panorama_to_rvt_rotation_deg',
    'panorama_to_rvt_scale',
    'panorama_to_rvt_swap_xy',
    'panorama_to_rvt_flip_x',
    'panorama_to_rvt_flip_y',
    'panorama_to_rvt_flip_z'
  ].some((key) => customData[key] !== undefined && customData[key] !== null);
};

const getCoordinateFrames = (customData: Record<string, any>): Record<string, CoordinateFrameMetadata> => {
  const frames = customData.coordinate_frames || customData.coordinateFrames || {};
  return frames && typeof frames === 'object' ? frames : {};
};

const coerceFrame = (
  frames: Record<string, CoordinateFrameMetadata>,
  frameId: CoordinateFrameId,
  fallback: CoordinateFrameMetadata
): CoordinateFrameMetadata => {
  const frame = frames[frameId] || {};
  return {
    id: frame.id || frameId,
    ...fallback,
    ...frame,
    unit: normalizeUnit(frame.unit ?? fallback.unit),
    bounds: frame.bounds || fallback.bounds,
    globalOffset: frame.globalOffset || fallback.globalOffset,
    notes: Array.isArray(frame.notes) ? frame.notes : fallback.notes
  };
};

const hasNativeCoordinateFrameMetadata = (customData: Record<string, any>): boolean => {
  const frames = getCoordinateFrames(customData);
  const diagnostics = customData.coordinate_diagnostics || customData.coordinateDiagnostics || {};
  const hasPanoramaFrame = Boolean(frames.panorama_raw || frames.e57_pose || diagnostics.panorama_raw);
  const hasRVTFrame = Boolean(frames.rvt_model || frames.autodesk_viewer || diagnostics.rvt_model || diagnostics.autodesk_viewer);
  return hasPanoramaFrame && hasRVTFrame;
};

const scaleFromUnits = (sourceUnit: CoordinateUnit, targetUnit: CoordinateUnit): number | null => {
  const sourceMeters = unitToMeters(sourceUnit);
  const targetMeters = unitToMeters(targetUnit);
  if (!sourceMeters || !targetMeters) return null;
  return sourceMeters / targetMeters;
};

const hasNonUnitScale = (scale: CoordinatePoint3D): boolean => {
  return Math.max(Math.abs(scale.x - 1), Math.abs(scale.y - 1), Math.abs(scale.z - 1)) > 1e-9;
};

export class CoordinateTransformService {
  hasExplicitPanoramaToRVTTransform(options: TransformResolutionOptions): boolean {
    return hasExplicitPanoramaToRVTConfig(getCustomData(options));
  }

  getCoordinateAlignmentDiagnostics(options: TransformResolutionOptions): CoordinateAlignmentDiagnostics {
    const customData = getCustomData(options);
    const frames = getCoordinateFrames(customData);
    const hasLegacyOverride = hasExplicitPanoramaToRVTConfig(customData);
    const sourceFrame = coerceFrame(frames, 'panorama_raw', {
      id: 'panorama_raw',
      kind: 'e57_pose',
      unit: 'unknown',
      source: 'missing'
    });
    const targetFrame = coerceFrame(frames, 'rvt_model', {
      id: 'rvt_model',
      kind: 'revit_model',
      unit: 'unknown',
      source: 'missing'
    });
    const viewerFrame = coerceFrame(frames, 'autodesk_viewer', {
      id: 'autodesk_viewer',
      kind: 'viewer_space',
      unit: targetFrame.unit || 'unknown',
      source: 'missing'
    });
    const transform = this.resolvePanoramaToRVTModelTransform(options);
    const nativeMetadataAvailable = hasNativeCoordinateFrameMetadata(customData);
    const sourceUnit = normalizeUnit(sourceFrame.unit);
    const targetUnit = normalizeUnit(targetFrame.unit);
    const unitScale = transform.scale;
    const notes: string[] = [];
    const rawDiagnostics = customData.coordinate_diagnostics || customData.coordinateDiagnostics;
    const hasFailedDiagnostics = Boolean(
      rawDiagnostics?.status === 'failed' ||
      rawDiagnostics?.panorama_raw?.status === 'failed' ||
      rawDiagnostics?.rvt_model?.status === 'failed' ||
      rawDiagnostics?.autodesk_viewer?.status === 'failed'
    );

    if (sourceFrame.source === 'missing') {
      notes.push('No panorama coordinate-frame metadata is stored for this site.');
    }
    if (targetFrame.source === 'missing') {
      notes.push('No RVT coordinate-frame metadata is stored for this site.');
    }
    if (sourceUnit === 'unknown' || targetUnit === 'unknown') {
      notes.push('One or more coordinate units are unknown; native alignment may require calibration.');
    }
    if (hasLegacyOverride) {
      notes.push('A manual Panorama to RVT override is active and takes precedence over native metadata.');
    } else if (hasNonUnitScale(unitScale)) {
      notes.push(`Native unit conversion is active: ${sourceUnit} to ${targetUnit}.`);
    } else if (nativeMetadataAvailable) {
      notes.push('Native frame metadata is active with no unit scaling.');
    }
    if (hasFailedDiagnostics) {
      notes.push('One or more coordinate metadata extraction steps failed.');
    }

    let status: CoordinateAlignmentStatus = 'needs_calibration';
    if (hasLegacyOverride) {
      status = 'legacy_override';
    } else if (hasFailedDiagnostics) {
      status = 'failed';
    } else if (nativeMetadataAvailable) {
      status = hasNonUnitScale(unitScale) ? 'native_with_unit_conversion' : 'native';
    }

    return {
      status,
      transformSource: status,
      sourceFrame,
      targetFrame,
      viewerFrame,
      transform,
      unitScale,
      hasLegacyOverride,
      notes,
      updatedAt: rawDiagnostics?.updatedAt || sourceFrame.updatedAt || targetFrame.updatedAt,
      raw: rawDiagnostics
    };
  }

  getAssetTransformCatalog(options: TransformResolutionOptions): AssetCoordinateTransformCatalog {
    const customData = getCustomData(options);

    return {
      transforms: {
        panoramaToRVTModel: this.resolvePanoramaToRVTModelTransform(options)
      },
      legacy: {
        geotiff: {
          xOffset: coerceNumber(customData.geotiff_x_offset, 0),
          yOffset: coerceNumber(customData.geotiff_y_offset, 0),
          coordinateScale: coerceNumber(customData.geotiff_coordinate_scale, 1),
          rotationOffsetDeg: coerceNumber(customData.geotiff_rotation_offset, 0),
          movementScale: coerceNumber(customData.geotiff_movement_scale, 1),
          useTfw: coerceBoolean(customData.geotiff_use_tfw, false),
          useWorldCoordinates: typeof customData.geotiff_use_world_coordinates === 'boolean'
            ? customData.geotiff_use_world_coordinates
            : null,
          swapAxes: coerceBoolean(customData.geotiff_swap_axes, false),
          flipX: coerceBoolean(customData.geotiff_flip_x, false),
          flipY: coerceBoolean(customData.geotiff_flip_y, false),
          disableCoordSync: coerceBoolean(customData.geotiff_disable_coord_sync, false)
        }
      }
    };
  }

  resolvePanoramaToRVTModelTransform(options: TransformResolutionOptions): PlanarSimilarityTransform {
    const customData = getCustomData(options);
    const configured = customData.coordinate_transforms?.panorama_to_rvt_model || customData.panorama_to_rvt_model;
    const legacyConfig = {
      translation: {
        x: customData.panorama_to_rvt_offset_x,
        y: customData.panorama_to_rvt_offset_y,
        z: customData.panorama_to_rvt_offset_z
      },
      rotationDeg: customData.panorama_to_rvt_rotation_deg,
      scale: customData.panorama_to_rvt_scale,
      swapXY: customData.panorama_to_rvt_swap_xy,
      flipX: customData.panorama_to_rvt_flip_x,
      flipY: customData.panorama_to_rvt_flip_y,
      flipZ: customData.panorama_to_rvt_flip_z
    };

    if (configured || hasExplicitPanoramaToRVTConfig(customData)) {
      const translationSource =
        configured?.translation ??
        configured?.offset ??
        (legacyConfig.translation.x !== undefined || legacyConfig.translation.y !== undefined || legacyConfig.translation.z !== undefined
          ? legacyConfig.translation
          : ZERO_POINT);

      return {
        sourceFrame: configured?.sourceFrame || 'panorama_raw',
        targetFrame: configured?.targetFrame || 'rvt_model',
        translation: coercePoint(translationSource, ZERO_POINT),
        rotationDeg: coerceNumber(configured?.rotationDeg ?? configured?.rotation_deg ?? legacyConfig.rotationDeg, 0),
        scale: coerceScale(configured?.scale ?? legacyConfig.scale ?? UNIT_SCALE),
        swapXY: coerceBoolean(configured?.swapXY ?? configured?.swap_xy ?? legacyConfig.swapXY, false),
        flipX: coerceBoolean(configured?.flipX ?? configured?.flip_x ?? legacyConfig.flipX, false),
        flipY: coerceBoolean(configured?.flipY ?? configured?.flip_y ?? legacyConfig.flipY, false),
        flipZ: coerceBoolean(configured?.flipZ ?? configured?.flip_z ?? legacyConfig.flipZ, false)
      };
    }

    const frames = getCoordinateFrames(customData);
    const nativeMetadataAvailable = hasNativeCoordinateFrameMetadata(customData);
    if (nativeMetadataAvailable) {
      const sourceFrame = coerceFrame(frames, 'panorama_raw', {
        id: 'panorama_raw',
        kind: 'e57_pose',
        unit: 'm',
        source: 'e57_pose_translation'
      });
      const targetFrame = coerceFrame(frames, 'rvt_model', {
        id: 'rvt_model',
        kind: 'revit_model',
        unit: 'ft',
        source: 'autodesk_revit_default'
      });
      const unitScale = scaleFromUnits(sourceFrame.unit || 'unknown', targetFrame.unit || 'unknown') ?? 1;

      return {
        sourceFrame: sourceFrame.id || 'panorama_raw',
        targetFrame: targetFrame.id || 'rvt_model',
        translation: { ...ZERO_POINT },
        rotationDeg: 0,
        scale: { x: unitScale, y: unitScale, z: unitScale },
        swapXY: false,
        flipX: false,
        flipY: false,
        flipZ: false
      };
    }

    const fallbackReference =
      coercePoint(customData.panorama_to_rvt_shared_reference_point, ZERO_POINT) ||
      ZERO_POINT;

    const sharedReference = hasMeaningfulMagnitude(fallbackReference)
      ? fallbackReference
      : hasMeaningfulMagnitude(customData.ifc_shared_reference_point)
        ? coercePoint(customData.ifc_shared_reference_point, ZERO_POINT)
        : options.fallbackRVTModelBoundsMin
          ? coercePoint(options.fallbackRVTModelBoundsMin, ZERO_POINT)
          : { ...ZERO_POINT };

    return {
      sourceFrame: 'panorama_raw',
      targetFrame: 'rvt_model',
      translation: coercePoint(sharedReference, ZERO_POINT),
      rotationDeg: 0,
      scale: { ...UNIT_SCALE },
      swapXY: false,
      flipX: false,
      flipY: false,
      flipZ: false
    };
  }

  transformPanoramaToRVTModel(
    point: CoordinatePoint3D,
    options: TransformResolutionOptions
  ): TransformResult {
    const customData = getCustomData(options);
    const hasExplicitTransform = hasExplicitPanoramaToRVTConfig(customData);
    const hasNativeTransform = hasNativeCoordinateFrameMetadata(customData);
    const allowImplicitFallback = options.allowImplicitFallback === true;
    const fallbackBoundsMin = options.fallbackRVTModelBoundsMin ? coercePoint(options.fallbackRVTModelBoundsMin, ZERO_POINT) : null;
    const transform = this.resolvePanoramaToRVTModelTransform(options);

    if (!hasExplicitTransform && !hasNativeTransform && !allowImplicitFallback) {
      return {
        point: { ...point },
        transform: { ...IDENTITY_PANORAMA_TO_RVT_TRANSFORM, translation: { ...ZERO_POINT }, scale: { ...UNIT_SCALE } },
        transformSource: 'identity'
      };
    }

    let transformSource: CoordinateTransformSource = 'identity';
    if (hasExplicitTransform) {
      transformSource = 'legacy_override';
    } else if (hasNativeTransform) {
      transformSource = hasNonUnitScale(transform.scale) ? 'native_with_unit_conversion' : 'native';
    } else if (hasMeaningfulMagnitude(customData.ifc_shared_reference_point)) {
      transformSource = 'shared-reference';
    } else if (hasMeaningfulMagnitude(fallbackBoundsMin)) {
      transformSource = 'model-bounds';
    }

    const shouldSkipFallbackTranslation =
      !hasExplicitTransform &&
      hasMeaningfulMagnitude(transform.translation) &&
      hasMeaningfulMagnitude(point);

    const effectiveTransform: PlanarSimilarityTransform = shouldSkipFallbackTranslation
      ? {
          ...transform,
          translation: { ...ZERO_POINT }
        }
      : transform;

    return {
      point: this.applyPlanarSimilarityTransform(point, effectiveTransform),
      transform: effectiveTransform,
      transformSource
    };
  }

  transformRVTModelToPanorama(
    point: CoordinatePoint3D,
    options: TransformResolutionOptions
  ): TransformResult {
    const customData = getCustomData(options);
    const hasExplicitTransform = hasExplicitPanoramaToRVTConfig(customData);
    const hasNativeTransform = hasNativeCoordinateFrameMetadata(customData);
    const allowImplicitFallback = options.allowImplicitFallback === true;
    const fallbackBoundsMin = options.fallbackRVTModelBoundsMin ? coercePoint(options.fallbackRVTModelBoundsMin, ZERO_POINT) : null;
    const transform = this.resolvePanoramaToRVTModelTransform(options);

    if (!hasExplicitTransform && !hasNativeTransform && !allowImplicitFallback) {
      return {
        point: { ...point },
        transform: { ...IDENTITY_PANORAMA_TO_RVT_TRANSFORM, translation: { ...ZERO_POINT }, scale: { ...UNIT_SCALE } },
        transformSource: 'identity'
      };
    }

    let transformSource: CoordinateTransformSource = 'identity';
    if (hasExplicitTransform) {
      transformSource = 'legacy_override';
    } else if (hasNativeTransform) {
      transformSource = hasNonUnitScale(transform.scale) ? 'native_with_unit_conversion' : 'native';
    } else if (hasMeaningfulMagnitude(customData.ifc_shared_reference_point)) {
      transformSource = 'shared-reference';
    } else if (hasMeaningfulMagnitude(fallbackBoundsMin)) {
      transformSource = 'model-bounds';
    }

    return {
      point: this.applyInversePlanarSimilarityTransform(point, transform),
      transform,
      transformSource
    };
  }

  applyPlanarSimilarityTransform(
    point: CoordinatePoint3D,
    transform: PlanarSimilarityTransform
  ): CoordinatePoint3D {
    let x = point.x;
    let y = point.y;
    let z = point.z;

    if (transform.swapXY) {
      [x, y] = [y, x];
    }

    if (transform.flipX) x = -x;
    if (transform.flipY) y = -y;
    if (transform.flipZ) z = -z;

    x *= transform.scale.x;
    y *= transform.scale.y;
    z *= transform.scale.z;

    const rotationRad = (transform.rotationDeg * Math.PI) / 180;
    const cos = Math.cos(rotationRad);
    const sin = Math.sin(rotationRad);

    const rotatedX = x * cos - y * sin;
    const rotatedY = x * sin + y * cos;

    return {
      x: rotatedX + transform.translation.x,
      y: rotatedY + transform.translation.y,
      z: z + transform.translation.z
    };
  }

  applyInversePlanarSimilarityTransform(
    point: CoordinatePoint3D,
    transform: PlanarSimilarityTransform
  ): CoordinatePoint3D {
    let x = point.x - transform.translation.x;
    let y = point.y - transform.translation.y;
    let z = point.z - transform.translation.z;

    const rotationRad = (transform.rotationDeg * Math.PI) / 180;
    const cos = Math.cos(rotationRad);
    const sin = Math.sin(rotationRad);

    const unrotatedX = x * cos + y * sin;
    const unrotatedY = -x * sin + y * cos;

    x = transform.scale.x !== 0 ? unrotatedX / transform.scale.x : unrotatedX;
    y = transform.scale.y !== 0 ? unrotatedY / transform.scale.y : unrotatedY;
    z = transform.scale.z !== 0 ? z / transform.scale.z : z;

    if (transform.flipX) x = -x;
    if (transform.flipY) y = -y;
    if (transform.flipZ) z = -z;

    if (transform.swapXY) {
      [x, y] = [y, x];
    }

    return { x, y, z };
  }
}

export const coordinateTransformService = new CoordinateTransformService();
